import { ExternalLink, Phone, ArrowRight, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import { PageHeader } from "./Services";
import { SITE } from "../lib/site";

export default function NotreSociete() {
  return (
    <div data-testid="notre-societe-page">
      <SeoHead
        title="Notre société — Termite Habitat 33 | Termite Bordeaux"
        description="Termite Habitat 33 est le cabinet d'expertise spécialisé dans le traitement des termites, du bois et de l'humidité en Gironde. Découvrez notre site officiel."
        canonical={SITE.url + "/notre-societe"}
        keywords={["Termite Habitat 33", "société termites Bordeaux", "entreprise termites Gironde"]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Notre société", url: SITE.url + "/notre-societe" },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE.legalName,
          url: SITE.parentSite,
          telephone: SITE.phoneRaw,
          email: SITE.email,
          sameAs: [SITE.parentSite, SITE.facebook],
          address: {
            "@type": "PostalAddress",
            streetAddress: SITE.address,
            postalCode: SITE.postalCode,
            addressLocality: SITE.city,
            addressRegion: SITE.region,
            addressCountry: "FR",
          },
        }}
      />
      <PageHeader
        eyebrow="Notre société"
        title="Termite Habitat 33 — cabinet d'expert."
        intro="Cabinet d'expertise spécialisé dans le traitement des termites, du bois, de la mérule et de l'humidité en Gironde."
      />
      <section className="container-x pt-8">
        <figure className="max-w-3xl mx-auto bg-white border border-[#D5CABD] rounded-sm overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_wonderful-greider-8/artifacts/h9ms8dul_20251029_101631.jpg"
            alt="Panneau d'information Termite Habitat 33 installé en Gironde"
            loading="lazy"
            className="w-full h-auto object-cover"
            data-testid="societe-panel-photo"
          />
          <figcaption className="p-4 text-xs text-gray-600 italic">
            Panneau d'information Termite Habitat 33 installé en bord de plan d'eau girondin —
            intervention rapide sur l'ensemble du département.
          </figcaption>
        </figure>
      </section>
      <section className="container-x py-16 grid lg:grid-cols-3 gap-10">
        <article className="lg:col-span-2 prose-article">
          <p>
            <strong>{SITE.legalName}</strong> est la société qui édite ce portail local{" "}
            <em>termitebordeaux.fr</em> ainsi que le site officiel{" "}
            <a href={SITE.parentSite} target="_blank" rel="noopener">{SITE.parentSiteLabel}</a>.
          </p>
          <p>
            Implantée à Parempuyre, à dix minutes du centre de Bordeaux, notre équipe intervient
            sur l'ensemble du département de la Gironde (33) : Bordeaux Métropole, Médoc,
            Libournais, Bazadais, Bassin d'Arcachon, Bordelais, Blayais et Entre-deux-mers.
          </p>
          <h2>Nos spécialités</h2>
          <ul>
            <li>Traitement des termites souterrains (injection, pulvérisation, pièges‑appâts).</li>
            <li>Traitement curatif et préventif des bois de charpente (capricornes, vrillettes).</li>
            <li>Traitement de la mérule et des champignons lignivores.</li>
            <li>Audit et traitement de l'humidité (remontées capillaires, infiltrations, condensation).</li>
            <li>Expertise parasitaire obligatoire avant vente immobilière.</li>
          </ul>
          <h2>Notre garantie</h2>
          <p>
            {SITE.guarantee}. Concrètement, si vous constatez une nouvelle infestation dans les 5
            ans suivant notre intervention, nous revenons traiter votre bien à nos frais — sans
            le moindre euro à votre charge. Cette garantie traduit la confiance que nous plaçons
            dans nos protocoles.
          </p>
          <h2>Pourquoi un site dédié à Bordeaux ?</h2>
          <p>
            <em>termitebordeaux.fr</em> est notre portail local expert, dédié aux propriétaires et
            professionnels de Bordeaux et de la métropole girondine. Il vous donne accès à des
            ressources spécifiques : cartographie des quartiers à risque, FAQ locale, articles
            d'expertise sur le bâti bordelais.
          </p>
          <p>
            Pour découvrir nos chantiers, photos avant/après, témoignages complets et coordonnées
            détaillées, visitez notre site officiel :{" "}
            <a href={SITE.parentSite} target="_blank" rel="noopener">{SITE.parentSiteLabel}</a>.
          </p>
          <h2>Nous suivre</h2>
          <p>
            <a href={SITE.parentSite} target="_blank" rel="noopener">Site officiel — {SITE.parentSiteLabel}</a>{" "}·{" "}
            <a href={SITE.facebook} target="_blank" rel="noopener">Page Facebook</a>
          </p>
        </article>

        <aside className="space-y-5">
          <div className="bg-gradient-to-br from-[#2B2118] to-[#3a2a1d] text-white p-6 rounded-sm border border-[#7A2E39]/40">
            <div className="eyebrow text-[#E8DCC9] mb-2">Site officiel</div>
            <h3 className="font-serif text-2xl mb-3">Termite Habitat 33</h3>
            <p className="text-sm text-[#E8DCC9]/85 mb-5">
              Découvrez nos réalisations, témoignages et coordonnées sur notre site principal.
            </p>
            <a
              href={SITE.parentSite}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center justify-center gap-2 bg-[#7A2E39] hover:bg-[#5E222A] px-4 py-2.5 w-full rounded-sm text-sm font-bold"
              data-testid="ns-parent-link"
            >
              {SITE.parentSiteLabel} <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:bg-white/10 px-4 py-2.5 w-full rounded-sm text-sm font-bold mt-2"
              data-testid="ns-fb-link"
            >
              <Facebook className="w-4 h-4" /> Facebook
            </a>
          </div>

          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm">
            <div className="eyebrow mb-2">Contact direct</div>
            <a href={`tel:${SITE.phoneRaw}`} className="btn-primary w-full mb-2" data-testid="ns-call">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <Link to="/contact" className="btn-ghost w-full">
              Demander une expertise <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </aside>
      </section>
      <CTABand />
    </div>
  );
}
